// Local persistence seam — swap here for advanced/service (ADR-032).
import { useCallback, useEffect, useRef, useState } from 'react'
import { clearItem, getItem, setItem } from 'src/legacy/lib/localStorageWithTTL'

// An expiring entry uses Standard's TTL store, which the dashboard sweeps on load.
type PersistOptions = { expiresInHours?: number }

const coerce = <T>(parsed: unknown, fallback: T): T => {
  if (parsed == null) {
    return fallback
  }
  if (Array.isArray(fallback) && !Array.isArray(parsed)) {
    return fallback
  }

  return parsed as T
}

export const readLocal = <T>(key: string, fallback: T, { expiresInHours }: PersistOptions = {}): T => {
  try {
    if (expiresInHours) {
      return coerce(getItem(key), fallback)
    }
    const raw = window.localStorage.getItem(key)

    return raw ? coerce(JSON.parse(raw), fallback) : fallback
  } catch {
    return fallback
  }
}

export const writeLocal = <T>(key: string, value: T, { expiresInHours }: PersistOptions = {}): void => {
  try {
    if (expiresInHours) {
      setItem(key, value, expiresInHours)
      return
    }
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Quota/private browsing: lose save rather than throw.
  }
}

export const clearLocal = (key: string): void => {
  try {
    clearItem(key)
  } catch {
    // Private browsing: nothing was stored.
  }
}

// Same-tab sync — the storage event only fires across tabs (CHANGELOG 40).
type Listener = (value: unknown) => void
const listenersByKey = new Map<string, Set<Listener>>()

const subscribe = (key: string, listener: Listener): (() => void) => {
  if (!listenersByKey.has(key)) {
    listenersByKey.set(key, new Set())
  }
  listenersByKey.get(key)?.add(listener)

  return () => listenersByKey.get(key)?.delete(listener)
}

const publish = (key: string, value: unknown): void => listenersByKey.get(key)?.forEach((listener) => listener(value))

export const usePersistedState = <T>(key: string, fallback: T, options: PersistOptions = {}) => {
  const { expiresInHours } = options
  const [value, setValue] = useState<T>(() => readLocal(key, fallback, options))
  // Re-read when key changes during render, not in an effect (ADR-048).
  const [loadedKey, setLoadedKey] = useState(key)
  if (loadedKey !== key) {
    setLoadedKey(key)
    setValue(readLocal(key, fallback, options))
  }
  const current = useRef(value)
  current.current = value
  const fallbackValue = useRef(fallback)
  fallbackValue.current = fallback

  useEffect(
    () =>
      subscribe(key, (next) => {
        current.current = next as T
        setValue(next as T)
      }),
    [key]
  )

  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      const resolved = typeof next === 'function' ? (next as (prev: T) => T)(current.current) : next
      current.current = resolved
      setValue(resolved)
      // Standard's TTL readers count any stored entry as live, so an expiring value back at its fallback is removed.
      if (expiresInHours && Object.is(resolved, fallbackValue.current)) {
        clearLocal(key)
      } else {
        writeLocal(key, resolved, { expiresInHours })
      }
      publish(key, resolved)
    },
    [key, expiresInHours]
  )

  return [value, set] as const
}
